import { HttpErrorResponse } from '@angular/common/http';
import { EventEmitter } from '@angular/core';
import { Params } from '@angular/router';
import { I18NextService } from 'angular-i18next';
import { BehaviorSubject, combineLatest, MonoTypeOperatorFunction, Observable, of, OperatorFunction, pipe, throwError, UnaryFunction } from 'rxjs';
import { catchError, filter, pluck, startWith, switchMap, tap } from 'rxjs/operators';
import { SetSelectedAgencyHeaderInterceptor } from '../layout/providers/set-selected-agency-header.interceptor';
import { GlobalActions } from './global.actions';
import { RouteActions } from './route.actions';
import { ReferencesService } from './references.service';

export const pluckIf = (...properties: string[]) =>
    pipe(
        pluck(...properties),
        filter(result => !!result)
    );

export const pluckIfNot = (...properties: string[]) =>
    pipe(
        pluck(...properties),
        filter(result => !result)
    );
export const isCopyRequest = () =>
    pipe(
        filter(result => 'copy' in <any>result)
    );
export const isNotCopyRequest = () =>
    pipe(
        filter(result => !('copy' in <any>result))
    );
export const hasQueryParams = (...queryParametersNames: string[]) =>
    pipe(
        filter((params: Params) => Object.keys(params).some(param => queryParametersNames.some(queryParam => queryParam === param)))
    );
export const hasNotQueryParams = () =>
    pipe(
        filter((params: Params) => Object.keys(params).length === 0)
    );

export function switchIfNotEmpty<T>(parameterName: string, action: (param: string) => Observable<T>): MonoTypeOperatorFunction<T> {
    return pipe(pluckIf(parameterName), switchMap(action));
}

export function switchIfEmpty<T>(parameterName: string, action: (params?: Params) => Observable<T>): MonoTypeOperatorFunction<T> {
    return pipe(pluckIfNot(parameterName), switchMap(action));
}

/**
 * Обертка над оператором `startWith`, приводящая значение к типу данных потока.
 * Нужна чтобы не засорять код явным приведением null к типу данных потока, которая есть из-за https://github.com/ReactiveX/rxjs/issues/4772
 * Если когда-нибудь эту issue закроют (судя по deprecated к версии 7 эту перегрузку оператора просто удалят), то и этот оператор можно будет удалить вернув `startWith(null)`
 */
export function startWithNull<T>(): OperatorFunction<T, T> {
    return startWith(null as T);
}

export function skipEmptyValues<T>(): OperatorFunction<T, T> {
    return filter((value: T) => !!value);
}

export function tapImpersonationHeader<T extends CanonicalStateWithTraits>(): OperatorFunction<T, T> {
    return tap((state: T) => {
        setImpersonationHeader(state);
    });
}

export function setImpersonationHeader(state: CanonicalStateWithTraits): void {
    if (!state) {
        SetSelectedAgencyHeaderInterceptor.SELECTED_AGENCY_CODE = null;
        return;
    }
    SetSelectedAgencyHeaderInterceptor.SELECTED_AGENCY_CODE = state.canonicalInfo && state.canonicalInfo.agency ? state.canonicalInfo.agency.code : null;
}

/**
 * Оператор, выставляющий @param loading в false при завершении запроса (успех, ошибка, не НЕ отмена). На случай ошибки возвращает пустой массив.
 *
 * Используется для ng-select чтобы корректно отработать флаг loading.
 * Оператор finalize не подходит потому что он вызывается на cancel запроса, который делается при вводе в фильтр ng-select значений.
 * При этом catchError тоже нужен иначе ng-select остается в неоперабельном состоянии из-за зависшего флага loading
 *
 * @param loading EventEmitter переданный в ng-select для признака [loading]
 */
export function tapLoadingAndEmptyOnError<T>(loading: EventEmitter<boolean>): OperatorFunction<T[], T[]> {
    return pipe(
        tap(() => {
            loading.emit(false);
        }),
        catchError(() => {
            loading.emit(false);
            return of([]);
        })
    );
}

export function redirectToNotFoundPageIf404<T>(routerActions: RouteActions): MonoTypeOperatorFunction<T> {
    return pipe(
        catchError(err => {
            if (err instanceof HttpErrorResponse && err.status === 404) {
                routerActions.goToNotFound();
            }
            return throwError(err);
        })
    );
}

export function redirectToAccessDeniedPageIf403<T>(routerActions: RouteActions,
                                                   requestType?: string,
                                                   description?: string,
                                                   buttonTitle?: string,
                                                   buttonUrl?: string): MonoTypeOperatorFunction<T> {
    return catchError(err => {
        if (err instanceof HttpErrorResponse && err.status === 403) {
            description ??= err.error;
            routerActions.goToAccessDenied({ isError: true, description, requestType, buttonTitle, buttonUrl });
        }
        return throwError(err);
    });
}

export function catchErrorFromSigma<T>(
    requestInSigmaIsPending$: BehaviorSubject<boolean>,
    sigmaIsNotResponding$: BehaviorSubject<boolean>,
    sigmaNotRespondingTextKey: string,
    i18next: I18NextService
): MonoTypeOperatorFunction<T> {
    return pipe(
        catchError(error => {
            requestInSigmaIsPending$.next(false);
            if (error.status === 500) {
                GlobalActions.displayError(i18next.t(sigmaNotRespondingTextKey), GlobalActions.InfiniteInterval);
                sigmaIsNotResponding$.next(true);
            }
            return of(null);
        })
    );
}

export function checkActiveRequests(referencesService: ReferencesService,
                                    i18next: I18NextService,
                                    routerActions: RouteActions,
                                    requestType: string,
                                    businessProcessTypes: Backend.BusinessProcessType[],
                                    getPrimaryRequests: (requestInfos: Backend.RequestValidationInfo[]) => Backend.RequestValidationInfo
): UnaryFunction<Observable<CanonicalStateWithTraits>, Observable<[CanonicalStateWithTraits, Backend.RequestValidationInfo[]]>> {
    return pipe(
        switchMap((stateWithTraits: CanonicalStateWithTraits) => {
            const activeRequest = !stateWithTraits.canonicalInfo.agency?.code || !!stateWithTraits.canonicalInfo.correlationId;
            return activeRequest
                ? of([stateWithTraits, []])
                : combineLatest([of(stateWithTraits), referencesService.getActiveAgencyRequests(businessProcessTypes)]);
        }),
        tap((state: [CanonicalStateWithTraits, Backend.RequestValidationInfo[]]) => {
            const activeRequestInfo = getPrimaryRequests(state[1]);
            if (activeRequestInfo !== null) {
                if (activeRequestInfo.businessProcessType === 'CONF01V2') {
                    activeRequestInfo.businessProcessType = 'CONF01'
                } else if (activeRequestInfo.businessProcessType === 'API01V2') {
                    activeRequestInfo.businessProcessType = 'API01'
                }

                const requestUrl = `/${activeRequestInfo.businessProcessType.toLowerCase()}/${activeRequestInfo.correlationId}`;
                routerActions.goToAccessDenied({
                    isError: true,
                    description: i18next.t(`shared:accessDeniedPage.${requestType}.hasActiveRequests.description`,
                        { requestUrl, requestNumber: activeRequestInfo.requestNumber }),
                    requestType
                });
            }
        })
    )
}
